import { t } from '@lingui/core/macro';
import { TireSeason } from '@navio/nsdk';
import type { BookingStoreCar } from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard } from 'noddi-ui-web';
import { NavioTireSeasonChip, TIRE_SEASON_STYLE } from 'noddi-web';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { BookingCarSelectionButton } from './Car/BookingCarSelectionButton';

const SEASONS = [TireSeason.WINTER, TireSeason.SUMMER] as const;

const getSeasonLabel = (season: (typeof SEASONS)[number]) => (season === TireSeason.WINTER ? t`Winter` : t`Summer`);

type IncomingTireSeasonCar = {
  car: BookingStoreCar;
  selectedSeason: TireSeason | null | undefined;
};

type Props = {
  cars: IncomingTireSeasonCar[];
  onSelect: (car: BookingStoreCar, season: TireSeason) => void;
};

/**
 * Which wheel set goes into storage on a new tire hotel signup. Never preselected. One car picks a season; several
 * cars are listed under each season, like the multi-car service and pickup steps.
 */
export const IncomingTireSeasonChoice = ({ cars, onSelect }: Props) => {
  const [onlyCar] = cars;
  if (cars.length === 1 && onlyCar) {
    return (
      <div role='group' aria-label={t`Tire season`} className='flex flex-col gap-3'>
        {SEASONS.map((season) => (
          <SeasonOption
            key={season}
            season={season}
            isSelected={onlyCar.selectedSeason === season}
            onSelect={() => onSelect(onlyCar.car, season)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {SEASONS.map((season) => (
        <NoddiBasicCard key={season} className='flex flex-col gap-4'>
          <div className='flex items-center gap-3'>
            <NavioTireSeasonChip
              season={season}
              size='md'
              iconOnly
              translations={getCommonTranslations().seasonBadge}
            />
            <span className='text-lg font-medium'>{getSeasonLabel(season)}</span>
          </div>
          <div className='flex flex-col gap-2'>
            {cars.map(({ car, selectedSeason }) => (
              <BookingCarSelectionButton
                key={`${car.licensePlate.countryCode}-${car.licensePlate.number}`}
                car={car}
                isSelected={selectedSeason === season}
                onClick={() => onSelect(car, season)}
                licensePlateNumber={car.licensePlate.number}
                price=''
              />
            ))}
          </div>
        </NoddiBasicCard>
      ))}
    </div>
  );
};

/** Selected takes the booking flow's selected-card fill, as on the car step and in the time picker. */
const SeasonOption = ({
  season,
  isSelected,
  onSelect
}: {
  season: (typeof SEASONS)[number];
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <button
    type='button'
    // Toggle buttons, as in `NoddiSelectionCard`: a `radio` role would promise arrow-key focus this does not implement.
    aria-pressed={isSelected}
    onClick={onSelect}
    className={cn(
      'flex items-center gap-3 rounded-xl border p-4 text-left shadow-xs transition-colors focus-visible:ring-3 focus-visible:ring-primary focus-visible:outline-hidden',
      isSelected
        ? 'border-card-selected-background bg-card-selected-background text-card-selected-foreground'
        : 'border-input bg-card text-foreground hover:border-primary/60'
    )}
  >
    <span
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-md',
        TIRE_SEASON_STYLE[season].tintClassName
      )}
    >
      <NoddiIcon name={TIRE_SEASON_STYLE[season].icon} size='medium' />
    </span>
    <span className='min-w-0 flex-1 font-semibold'>{getSeasonLabel(season)}</span>
    <span
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full border-2',
        isSelected ? 'border-card-selected-foreground' : 'border-muted-foreground/30'
      )}
    >
      {isSelected && <span className='size-2 rounded-full bg-card-selected-foreground' />}
    </span>
  </button>
);
